import { useEffect } from "react";
import { syncStickyScrollbarWidth } from "../../utils/gridTableUx";

/**
 * Keep the mirrored My Vendors horizontal scrollbar in sync with the table
 * scroller (width + scrollLeft), reserving the sticky Actions column.
 *
 * @param {React.RefObject<HTMLElement | null>} tableContainerRef
 * @param {React.RefObject<HTMLElement | null>} stickyScrollbarRef
 * @param {boolean} enabled
 * @param {unknown} [layoutKey] - change to re-bind after layout/data shifts
 */
export default function useMyVendorsStickyScrollbarSync(
  tableContainerRef,
  stickyScrollbarRef,
  enabled,
  layoutKey
) {
  useEffect(() => {
    if (!enabled) return undefined;

    let cleanup = null;
    let attempts = 0;
    const maxAttempts = 20;

    const setupScrollSync = () => {
      const tableContainer = tableContainerRef.current;
      const stickyScrollbar = stickyScrollbarRef.current;
      if (!tableContainer || !stickyScrollbar) {
        attempts += 1;
        if (attempts < maxAttempts) setTimeout(setupScrollSync, 100);
        return;
      }

      const updateScrollbarWidth = () => {
        syncStickyScrollbarWidth(tableContainer, stickyScrollbar);
      };

      let scrollbarRaf = 0;
      const scheduleUpdateScrollbarWidth = () => {
        cancelAnimationFrame(scrollbarRaf);
        scrollbarRaf = requestAnimationFrame(updateScrollbarWidth);
      };

      const handleTableScroll = () => {
        stickyScrollbar.scrollLeft = tableContainer.scrollLeft;
      };
      const handleStickyScroll = () => {
        tableContainer.scrollLeft = stickyScrollbar.scrollLeft;
      };

      tableContainer.addEventListener("scroll", handleTableScroll);
      stickyScrollbar.addEventListener("scroll", handleStickyScroll);
      updateScrollbarWidth();
      stickyScrollbar.scrollLeft = tableContainer.scrollLeft;

      const timers = [50, 200, 600, 1500].map((delay) =>
        setTimeout(updateScrollbarWidth, delay)
      );
      const resizeObserver = new ResizeObserver(scheduleUpdateScrollbarWidth);
      resizeObserver.observe(tableContainer);
      window.addEventListener("resize", scheduleUpdateScrollbarWidth);

      cleanup = () => {
        cancelAnimationFrame(scrollbarRaf);
        timers.forEach(clearTimeout);
        resizeObserver.disconnect();
        tableContainer.removeEventListener("scroll", handleTableScroll);
        stickyScrollbar.removeEventListener("scroll", handleStickyScroll);
        window.removeEventListener("resize", scheduleUpdateScrollbarWidth);
      };
    };

    setupScrollSync();
    return () => {
      if (cleanup) cleanup();
    };
  }, [enabled, layoutKey, tableContainerRef, stickyScrollbarRef]);
}
